/* eslint-disable jsx-a11y/no-autofocus */
/* eslint-disable no-restricted-syntax */
import React, {
  ChangeEvent,
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import { get } from 'lodash';
import numeral from 'numeral';
import {
  Alert,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  Checkbox,
  Button,
  Box,
  Image,
  Container,
  IconButton,
  Flex,
  Text,
  SkeletonText,
  ModalBody,
  ModalFooter,
  useModal,
  useToast,
} from '@stockx/ui';
import { useCurrency, Money } from '@app/currency';
import { usePageType } from '@app/page';
import { usePostPortfolioItem } from '@app/transaction';
import { useExperiment } from '@app/experiments';
import { EXPERIMENTS } from '@app/config';
import MonthSelect from '@account/pages/Portfolio/components/MonthSelect';
import YearSelect from '@account/pages/Portfolio/components/YearSelect';
import SizeSelect from '@account/pages/Portfolio/components/SizeSelect';
import ConditionSelect from '@account/pages/Portfolio/components/ConditionSelect';
import PortfolioItemConstants from '@sell/const/PortfolioItem';
import { Products } from '@app/catalog';
import { EVENTS, trackEvent } from '@app/segment';
import { CloseIcon } from '@stockx/icons';
import { useCustomer } from '@app/user';
import { followedProductsList } from '@app/follow/state';
import { useFavoriteProduct } from '@app/favorite/hooks';
import { useSetRecoilState } from 'recoil';
import { formatPortfolioItemExpiration } from '@app/datetime';
import useErrorMessage from '@app/hooks/useErrorMessage';
import renderError from './utils/renderError';
import ProductSearch from './components/ProductSearch';
import RadioGroup from './components/RadioGroup';

const AddPortfolioItemModalForm = ({
  product,
  setTitleFromActionCode,
  setIsModalOpen,
  configurableProductId,
  setConfigurableProductId,
  action,
  setAction,
  setQueryProductId,
  error,
  setError,
  loading,
  setLoading,
  refetch,
}) => {
  const { t } = useTranslation();
  const customer = useCustomer();
  const { currency } = useCurrency();
  const pageType = usePageType();
  const { sx } = useModal();
  const toast = useToast();
  const getErrorMessage = useErrorMessage();
  const isFavoriteTestEnabled =
    useExperiment(EXPERIMENTS.ab_pirate_product_cell_favorite_web_v1) ===
    'true';

  const setProductIsFollowed = useSetRecoilState(followedProductsList);
  const {
    favoriteProduct,
    loading: favoriteLoading,
    data: favoriteData,
    error: favoriteError,
  } = useFavoriteProduct(product);

  const { postPortfolioItem } = usePostPortfolioItem({
    success: ({ PortfolioItem }) => {
      setTimeout(() => {
        setIsModalOpen(false);
        setProductIsFollowed({ [PortfolioItem?.product?.parentId]: true });

        toast({
          message: t('add_porfolio_item_added'),
          duration: 5000,
        });

        if (refetch) {
          refetch();
        }
      }, 500);
    },
    error: (err) => {
      setError(err);
      setLoading(false);
    },
  });

  const [majorCondition, setMajorCondition] = useState('2000');
  const [minorCondition, setMinorCondition] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('');
  const [amount, setAmount] = useState('');
  const [size, setSize] = useState(
    Products.getSimpleProductIdFromDefaultSize(
      product,
      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
      customer ? customer.defaultSize : '',
    ),
  );

  const [showDefaultCheckbox, setShowDefaultCheckbox] = useState(false);
  const [changeDefaultSize, setChangeDefaultSize] = useState(false);

  const formRef = useRef();

  useEffect(() => {
    if (favoriteData && !favoriteLoading) {
      setIsModalOpen(false);

      toast({
        message: t('add_porfolio_item_added'),
        duration: 5000,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favoriteData, favoriteLoading]);

  useEffect(() => {
    if (favoriteError) {
      setError(getErrorMessage(favoriteError));
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favoriteError]);

  useEffect(() => {
    if (product?.children && Object.keys(product?.children).length === 1) {
      setSize(Object.keys(product?.children)[0]);
    }
  }, [product]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!size) {
        setError(
          t('collect_form_no_size_selected_error', {
            sizeType: product?.sizeTitle,
          }),
        );
        return;
      }

      let condition = majorCondition;
      if (majorCondition !== '2000') {
        if (!Money.isNumeric(Number(minorCondition))) {
          setError(t('Please choose a Condition'));
          return;
        }

        condition = minorCondition;
      }

      if (action === 1000 && (!month || !year)) {
        setError(t('add_portfolio_item_date_error'));
        return;
      }

      if (action === 1000 && !amount) {
        setError(t('add_portfolio_item_price_error'));
        return;
      }

      const { uuid, productCategory, title } = get(
        product,
        `children[${size}]`,
      );

      setError(null);

      let date = '';
      if (Money.isNumeric(Number(month)) && Money.isNumeric(Number(year))) {
        date = formatPortfolioItemExpiration(new Date(+year, +month, 1));
      }

      let price = 0;
      if (Money.isNumeric(amount)) {
        // @ts-expect-error ts-migrate(2322) FIXME: Type 'string' is not assignable to type 'number'.
        price = amount;
      }

      const data = {
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        PortfolioItem: {
          localAmount: price,
          localCurrency: currency,
          matchedWithDate: date,
          condition,
          skuUuid: product?.children[size].uuid,
          action,
        },
      };

      const selectedShoeSize = product?.children?.[size]?.shoeSize;

      if (
        // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
        (customer.defaultSize === null || changeDefaultSize) &&
        selectedShoeSize
      ) {
        // @ts-expect-error ts-migrate(2339) FIXME: Property 'Customer' does not exist on type '{ time... Remove this comment to see the full error message
        data.Customer = { defaultSize: selectedShoeSize };
      }

      const productUUID = get(product, `id`);

      if (action === 1001) {
        trackEvent(EVENTS.PRODUCT_FOLLOWED, {
          vertical: productCategory,
          productUUID: product?.uuid,
          skuUUID: uuid,
          productName: title,
          screenName: pageType,
        });
      }

      if (action === 1000) {
        trackEvent(EVENTS.PRODUCT_ADDED_TO_PORTFOLIO, {
          vertical: productCategory,
          productUUID,
          skuUUID: uuid,
          productName: title,
          screenName: pageType,
          condition: condition === '2000' ? 'new' : 'used',
        });
      }

      setLoading(true);

      try {
        if (isFavoriteTestEnabled) {
          if (action === PortfolioItemConstants.actions.HOLD) {
            await postPortfolioItem(data, action);
          } else {
            await favoriteProduct({
              input: {
                action: 'ADD',
                items: [
                  {
                    productId: product?.id,
                    variantId: product?.children[size].uuid,
                  },
                ],
              },
              skipFavorite: !isFavoriteTestEnabled,
            });
          }
        } else {
          await postPortfolioItem(data, action);
        }
      } catch (e) {
        setLoading(false);
      }
    },
    [
      size,
      majorCondition,
      action,
      month,
      year,
      amount,
      product,
      setError,
      currency,
      customer?.defaultSize,
      changeDefaultSize,
      setLoading,
      t,
      minorCondition,
      pageType,
      postPortfolioItem,
    ],
  );

  const handleSearchSelect = useCallback(
    async (product) => {
      setQueryProductId(product?.id);
    },
    [setQueryProductId],
  );

  const handleNumberInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const amount = Money.convertFullToHalf(e.target.value) || '0';
    let trimmedAmount = amount.replace(/^0+(?!$)/, '');
    trimmedAmount = trimmedAmount.replace(/[,.]/g, '').substring(0, 8);
    trimmedAmount = parseInt(trimmedAmount, 10);
    if (trimmedAmount < 0) {
      trimmedAmount = 0;
    }

    setAmount(trimmedAmount);
  };

  const giveMeSearch = useCallback(
    (e) => {
      e.preventDefault();
      setConfigurableProductId(null);
    },
    [setConfigurableProductId],
  );

  if (!configurableProductId && !loading) {
    return (
      <>
        {renderError(error)}
        <form
          method="POST"
          // @ts-expect-error ts-migrate(2322) FIXME: Type 'MutableRefObject<undefined>' is not assignab... Remove this comment to see the full error message
          ref={formRef}
          autoComplete="off"
          id="collect-form"
          onSubmit={(e) => handleSubmit(e)}
        >
          <ProductSearch
            label={t('Which product would you like to add?')}
            onSelect={(e) => handleSearchSelect(e)}
          />
        </form>
      </>
    );
  }

  const conditionOptions = [
    {
      value: '2000',
      label: `${product?.condition
        ?.charAt(0)
        .toUpperCase()} ${product?.condition?.slice(1)}`,
    },
    {
      value: '-1',
      label: t('Other'),
    },
  ];

  const collectFields: JSX.Element[] = [];

  if (+action === +PortfolioItemConstants.actions.HOLD) {
    let warning;
    if (majorCondition !== '2000') {
      warning = (
        <Alert variant="warning-light" mb={4}>
          {t('add_to_portfolio_modal_used_item_disclaimer_1')}
        </Alert>
      );
    }

    collectFields.push(
      <FormControl>
        <FormLabel fontWeight="bold">{t('Condition')}</FormLabel>
        <RadioGroup
          // @ts-expect-error ts-migrate(2322) FIXME: Type '{ name: string; options: { value: string; la... Remove this comment to see the full error message
          name="PortfolioItem[condition]"
          options={conditionOptions}
          value={majorCondition}
          setValue={(value) => setMajorCondition(value)}
        />
        {warning}
      </FormControl>,
    );

    if (majorCondition && majorCondition !== '2000') {
      collectFields.push(
        <ConditionSelect
          key="minorCondition"
          name="PortfolioItem[condition]"
          setValue={(value) => setMinorCondition(value)}
        />,
      );
    }

    collectFields.push(
      <Flex alignItems="flex-end">
        <MonthSelect
          key="month"
          name="PortfolioItem[month]"
          setValue={(value) => setMonth(value)}
        />
        <YearSelect
          key="year"
          // @ts-expect-error ts-migrate(2322) FIXME: Type '{ key: string; name: string; setValue: (valu... Remove this comment to see the full error message
          name="PortfolioItem[year]"
          setValue={(value) => setYear(value)}
        />
      </Flex>,
    );

    collectFields.push(
      <FormControl mt={4}>
        <FormLabel fontWeight="bold" htmlFor={t('localAmount')}>
          {t('Purchase Price')}
        </FormLabel>
        <NumberInput precision={0}>
          <NumberInputField
            key="localAmount"
            name="PortfolioItem[localAmount]"
            id="localAmount"
            className="required"
            // @ts-expect-error ts-migrate(2365) FIXME: Operator '>=' cannot be applied to types 'string' ... Remove this comment to see the full error message
            value={amount && amount >= 0 ? numeral(amount).format('0') : ''}
            onChange={(val) => handleNumberInputChange(val)}
          />
        </NumberInput>
      </FormControl>,
    );
  }

  // @ts-expect-error ts-migrate(2322) FIXME: Type 'null' is not assignable to type 'Element'.
  let defaultCheckbox: JSX.Element = null;
  if (
    product?.shoeSize &&
    showDefaultCheckbox &&
    // @ts-expect-error ts-migrate(2531) FIXME: Object is possibly 'null'.
    customer.defaultSize != null
  ) {
    defaultCheckbox = (
      <FormControl>
        <FormLabel fontWeight="bold" htmlFor="change_default_size">
          {t('Set as Default Size')}
        </FormLabel>
        <Checkbox
          name=""
          id="change_default_size"
          onChange={() => setChangeDefaultSize(!changeDefaultSize)}
          checked={changeDefaultSize}
        />
      </FormControl>
    );
  }

  let childHasSize = false;
  if (product?.children) {
    for (const child in product.children) {
      if (product.children[child].shoeSize) {
        childHasSize = true;
        break;
      }
    }
  }

  return (
    // @ts-expect-error ts-migrate(2322) FIXME: Type 'MutableRefObject<undefined>' is not assignab... Remove this comment to see the full error message
    <form method="POST" ref={formRef} autoComplete="off" id="collect-form">
      {renderError(error)}
      <ModalBody sx={sx.modalBody} minHeight="255px">
        {!product || loading ? (
          <SkeletonText noOfLines={3} />
        ) : (
          <>
            <Container
              data-component="selected-item"
              w="100%"
              bg="neutral.white"
              border="1px"
              borderStyle="solid"
              borderColor="neutral.300"
              p={2}
            >
              <Flex justifyContent="space-between" position="relative">
                <Image
                  data-component="selected-item-img"
                  w={20}
                  maxH={32}
                  objectFit="contain"
                  mr={{ base: '2', md: '5' }}
                  src={product?.media?.thumbUrl}
                />
                <Box textTransform="uppercase" fontWeight="bold">
                  <Text fontSize="xs" fontWeight="normal" mb="1">
                    {product?.brand}
                  </Text>
                  <Text fontSize="lg">{product?.title}</Text>
                  <Text fontSize={{ base: 'md', md: 'sm' }} mt={[1, 0]}>
                    {product?.colorway}
                  </Text>
                </Box>
                <IconButton
                  aria-label={t('Close')}
                  size="lg"
                  mr="-2"
                  mt="-2"
                  icon={<CloseIcon />}
                  onClick={(e) => giveMeSearch(e)}
                />
              </Flex>
            </Container>
            <FormControl mt={4}>
              <FormLabel fontWeight="bold">{t('Add to')}</FormLabel>
              <RadioGroup
                // @ts-expect-error ts-migrate(2322) FIXME: Type '{ name: string; options: { value: number; la... Remove this comment to see the full error message
                name="PortfolioItem[state]"
                key="PortfolioItem[state]"
                options={[
                  {
                    value: PortfolioItemConstants.actions.HOLD,
                    label: t('Portfolio'),
                  },
                  {
                    value: PortfolioItemConstants.actions.WANT,
                    label: isFavoriteTestEnabled ? t('Favorite') : t('Follow'),
                  },
                ]}
                value={action}
                setValue={(value) => {
                  setTitleFromActionCode(value);
                  setAction(parseInt(value, 10));
                }}
              />
              {childHasSize ? (
                <SizeSelect
                  // @ts-expect-error ts-migrate(2322) FIXME: Type '{ name: string; product: any; setValue: (val... Remove this comment to see the full error message
                  name="PortfolioItem[product_id]"
                  product={product}
                  setValue={(value) => {
                    setSize(value);
                    setShowDefaultCheckbox(true);
                  }}
                />
              ) : null}
              {defaultCheckbox}
              <Box mt={4}>{collectFields}</Box>
            </FormControl>
          </>
        )}
      </ModalBody>
      <ModalFooter sx={sx.modalFooter}>
        <Flex justifyContent="space-between" width="100%" margin="0 !important">
          <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
            {t('Cancel')}
          </Button>
          <Button
            variant="primary"
            isLoading={loading}
            onClick={(e) => handleSubmit(e)}
          >
            {t('Confirm')}
          </Button>
        </Flex>
      </ModalFooter>
    </form>
  );
};

export default AddPortfolioItemModalForm;
